import { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { fetchProducts, imgSrc } from "@/lib/api";
import { waLink } from "@/lib/brand";
import { Reveal } from "./Motion";
import { CheckoutDialog } from "@/components/shop/CheckoutDialog";
import { PayInfo } from "@/components/shop/PayInfo";

const ProductCard = ({ p, idx, onBuy }) => (
  <Reveal delay={idx * 0.08}>
    <article data-testid={`product-card-${idx}`} className="group relative">
      <div className="relative aspect-[4/5] overflow-hidden rounded-[22px] bg-[#0e1120] [clip-path:inset(0_round_22px)]">
        <img src={imgSrc(p.image)} alt={p.name} loading="lazy" className="h-full w-full object-cover grayscale-[.35] transition-[transform,filter] duration-[1200ms] ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-110 group-hover:grayscale-0" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,transparent_25%,rgba(7,8,15,.85)_80%)]" />
        {p.category && <span className="absolute left-4 top-4 rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs backdrop-blur-md">{p.category}</span>}
        <div className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-3">
          <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight">{p.name}</h3>
          <span data-testid={`product-price-${idx}`} className="shrink-0 rounded-full bg-white/10 px-3 py-1.5 text-sm font-semibold backdrop-blur-md">{p.price ? `R$ ${p.price}` : "Consulte"}</span>
        </div>
      </div>
      {p.description && <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{p.description}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
        {p.price && <button data-testid={`product-buy-${idx}`} onClick={() => onBuy(p)} className="rounded-full grad-bg px-6 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">Comprar</button>}
        <a data-testid={`product-cta-${idx}`} href={waLink(`Olá! Tenho interesse no produto ${p.name}${p.price ? ` (R$ ${p.price})` : ""}. Pode me passar mais informações?`)} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 transition-colors hover:text-white">
        <MessageCircle className="h-4 w-4" /> {p.price ? "Tirar dúvidas" : "Consultar valor no WhatsApp"}
      </a>
      </div>
    </article>
  </Reveal>
);

export const Products = () => {
  const [items, setItems] = useState([]);
  const [buying, setBuying] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchProducts("speedmax").then((d) => { if (alive) setItems(d); });
    return () => { alive = false; };
  }, []);

  return (
    <section id="produtos" data-testid="products-section" className="relative border-t border-white/10 bg-[#0a0b15]">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-10 py-24 sm:py-36">
        <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs tracking-[0.35em] text-cyan-300">LOJA DE APARELHOS</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">Aparelhos para<br />ligar e assistir.</h2>
          </div>
          <p className="max-w-sm text-sm sm:text-base text-zinc-300">Fire Stick, TV Box e Android Stick. Envio para todo o Brasil.</p>
        </Reveal>
        {items.length === 0 ? (
          <p data-testid="products-empty" className="text-zinc-400">Novos produtos em breve. Fale com a gente no WhatsApp.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 sm:gap-6">
            {items.map((p, i) => <ProductCard key={p.id || i} p={p} idx={i} onBuy={setBuying} />)}
          </div>
        )}
        <div className="mt-16 border-t border-white/10 pt-8"><PayInfo /></div>
      </div>
      {buying && <CheckoutDialog product={buying} onClose={() => setBuying(null)} />}
    </section>
  );
};
